import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Instagram, Youtube, ExternalLink, MapPin } from 'lucide-react';
import { CongresoPublico } from '../types/congreso.types';
import { INSTITUCION_NOMBRE_COMPLETO } from '../constants';

interface FooterProps {
  congreso?: CongresoPublico | null;
}

export const Footer: React.FC<FooterProps> = ({ congreso }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-navy border-t border-slate-700/60 pt-16 pb-12 text-slate-300 text-sm relative overflow-hidden">
      {/* Glow de fondo sutil */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-brand-cyan/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          {/* Columna 1: Identidad Institucional y Co-Branding */}
          <div className="md:col-span-6 space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={congreso?.imagenLogoUrl || '/assets/logo-martillerosba.png'}
                alt={congreso?.nombre || 'MartillerosBA'}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/assets/logo-martillerosba.png';
                }}
              />
              <span className="font-heading font-extrabold text-white text-base uppercase tracking-wider">
                {congreso?.nombre || 'Congreso Inmobiliario'}
              </span>
            </div>

            <p className="text-xs text-slate-300 max-w-md leading-relaxed">
              Organizado por el{' '}
              <strong className="text-white font-semibold block mt-1">
                {INSTITUCION_NOMBRE_COMPLETO}
              </strong>
            </p>

            {/* Acceso a Sitio Web Oficial y Redes Sociales */}
            <div className="pt-2 flex flex-wrap items-center gap-4 text-xs">
              <a
                href="https://www.martillerosba.org.ar"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-sky hover:text-white transition-colors"
                title="Sitio Web Oficial Martilleros BA"
              >
                <Globe className="w-4 h-4 text-brand-cyan" />
                <span>www.martillerosba.org.ar</span>
                <ExternalLink className="w-3 h-3 opacity-60" />
              </a>

              <div className="flex items-center gap-3 pl-2 border-l border-slate-700">
                <a
                  href="https://www.instagram.com/martillerosba"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg bg-brand-surface hover:bg-brand-navy text-slate-300 hover:text-brand-cyan transition-colors"
                  aria-label="Instagram oficial Martilleros BA"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.youtube.com/@martillerosba"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg bg-brand-surface hover:bg-brand-navy text-slate-300 hover:text-red-400 transition-colors"
                  aria-label="Canal de YouTube oficial con transmisiones en vivo"
                >
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Columna 2: Navegación del Congreso */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="font-heading font-bold text-white text-xs uppercase tracking-wider">
              Navegación
            </h4>
            <ul className="space-y-2 text-xs">
              <li>
                <a href="#inicio" className="hover:text-brand-sky transition-colors">
                  Inicio
                </a>
              </li>
              <li>
                <a href="#agenda" className="hover:text-brand-sky transition-colors">
                  Programa Oficial
                </a>
              </li>
              <li>
                <a href="#disertantes" className="hover:text-brand-sky transition-colors">
                  Disertantes
                </a>
              </li>
              <li>
                <a href="#sede" className="hover:text-brand-sky transition-colors">
                  Sede del Evento
                </a>
              </li>
              <li>
                <a href="#faq" className="hover:text-brand-sky transition-colors">
                  Preguntas Frecuentes
                </a>
              </li>
              <li>
                <Link to="/ediciones" className="hover:text-brand-sky transition-colors">
                  Ediciones Anteriores
                </Link>
              </li>
            </ul>
          </div>

          {/* Columna 3: Sede y Datos del Evento */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="font-heading font-bold text-white text-xs uppercase tracking-wider">
              Sede Oficial
            </h4>
            <div className="space-y-2 text-xs text-slate-300">
              <div className="flex items-start gap-1.5">
                <MapPin className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium block">
                    {congreso?.sede || 'Sede a confirmar'}
                  </span>
                  {congreso?.domicilio && (
                    <span className="text-slate-400 block">
                      {congreso.domicilio}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Barra inferior de copyright y sede institucional */}
        <div className="pt-8 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div>
            &copy; {currentYear} {INSTITUCION_NOMBRE_COMPLETO}. Todos los derechos reservados.
          </div>
          <div className="flex items-center gap-4 text-slate-400">
            <span>Provincia de Buenos Aires</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

